/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import * as d3 from "d3";
import { DelayTaps, type EchoTap, PingPongTaps } from "@wbaudio/utils";
import { CreateChart } from "../../core/chart.ts";
import { InlineSize, ModeTheme, PadMargin } from "../../core/mode.ts";
import { useChart } from "../../core/useChart.ts";
import { GridLayer } from "../../layers/grid.ts";
import { BarsLayer } from "../../layers/bars.ts";
import { ChartPalette } from "../../utils/theme.ts";

export const EchoInlineSize = { ...InlineSize, pad: 2 };

function CreateEchoChart(container: HTMLElement) {
  const bars = BarsLayer({ id: "taps", gap: 0, barWidth: 2 });
  const chart = CreateChart(container, {
    height: EchoInlineSize.height,
    margin: PadMargin(EchoInlineSize.pad),
    x: d3.scaleLinear().domain([0, 1]),
    y: d3.scaleLinear().domain([0, 1]),
    theme: ModeTheme("inline"),
    layers: [GridLayer({ axis: "x", ticks: 4 }), bars],
  });
  return { chart, bars, destroy: chart.destroy };
}

function useEchoChart(taps: EchoTap[], maxTime: number, color: (tap: EchoTap) => string) {
  const { ref, chart } = useChart(CreateEchoChart, []);
  useEffect(() => {
    if (!chart) return;
    chart.bars.set(
      taps.map((tap) => ({ x0: tap.time, x1: tap.time, value: tap.amplitude, color: color(tap) })),
    );
    chart.chart.setDomain([0, maxTime]);
  }, [chart, taps, maxTime]);
  return ref;
}

export interface DelayEchoGraphProps {
  delayTime: number;
  feedback: number;
  maxTime: number;
  color?: string;
}

export function DelayEchoGraph(props: DelayEchoGraphProps) {
  const color = props.color ?? ChartPalette.blue;
  const ref = useEchoChart(
    DelayTaps(props.delayTime, props.feedback, props.maxTime),
    props.maxTime,
    () => color,
  );
  return <div ref={ref} />;
}

export interface PingPongEchoGraphProps {
  delayTimeLeft: number;
  delayTimeRight: number;
  feedback: number;
  maxTime: number;
  colorLeft?: string;
  colorRight?: string;
}

export function PingPongEchoGraph(props: PingPongEchoGraphProps) {
  const left = props.colorLeft ?? ChartPalette.blue;
  const right = props.colorRight ?? ChartPalette.green;
  const ref = useEchoChart(
    PingPongTaps(props.delayTimeLeft, props.delayTimeRight, props.feedback, props.maxTime),
    props.maxTime,
    (tap) => (tap.side === "left" ? left : right),
  );
  return <div ref={ref} />;
}
